import { transitionLog } from '../debug/transitionDebug.js';
import { expandCardImageInPlace, INPLACE_EXPAND_DURATION } from '../carousel/cardImageExpand.js';
import { getCardVisualSource, captureFrameDataUrl } from '../carousel/cardVideos.js';
import { stashChannelHandoff } from './channelHandoff.js';

/**
 * Transitions: card image expand (carousel), in-place world opens, and
 * MPA back-to-home. In-place worlds: WorldManager `closeWithPixelReveal`
 * (WebGL) + breadcrumb / logo. No WebGL in this module for MPA.
 */

class TransitionManagerClass {
  constructor() {
    this._tween = null; // reserved if we add non-GL tweens here later
    this._activeTransition = null;
    this._navigating = false;
    this._expandTl = null;
    this._inPlaceLayer = null;
    this._inPlaceActive = false;
    this._debugOp = 0;
    this._bfcacheBound = false;
    this._onPageShow = null;
  }

  isNavigating() {
    return this._navigating;
  }

  /**
   * One call per page after shell is ready. No WebGL to mount.
   */
  init() {
    this._bindBfcacheReset();
    transitionLog('init:ready', {
      path: typeof location !== 'undefined' ? location.pathname : '',
      hash: typeof location !== 'undefined' ? (location.hash || '') : '',
    });
    return Promise.resolve();
  }

  /**
   * MPA: assign URL. In-place (same document) return home uses WorldManager, not
   * this.
   */
  transitionOut(url) {
    this._debugOp += 1;
    const op = this._debugOp;
    if (!url || this._navigating) {
      transitionLog('transitionOut:skipped', {
        op,
        reason: !url ? 'no url' : 'navigating',
        hadUrl: Boolean(url),
        path: String(url),
        active: this._activeTransition,
        nav: this._navigating,
      });
      return Promise.resolve();
    }
    this._navigating = true;
    transitionLog('transitionOut:instant_nav', { op, url: String(url) });
    this._activeTransition = 'out';
    try {
      window.location.assign(String(url));
    } catch {
      this._navigating = false;
      this._activeTransition = null;
    }
    return Promise.resolve();
  }

  /**
   * MPA (travel): card grows to a fullscreen cover, we stash the card art +
   * theme for the next document, then navigate. The cover stays up until this
   * document unloads; the next page paints the same art first thing and runs
   * the channel wipe once it is ready — so there is never a blank frame.
   */
  expandFromCard(cardEl, url) {
    this._debugOp += 1;
    const op = this._debugOp;
    if (!cardEl || !url || this._navigating) {
      transitionLog('expandFromCard:skipped', {
        op,
        reason: !cardEl ? 'no card' : !url ? 'no url' : 'navigating',
        nav: this._navigating,
        url: String(url),
        active: this._activeTransition,
      });
      return Promise.resolve();
    }
    this._navigating = true;
    const strUrl = String(url);
    transitionLog('expandFromCard:start', { op, url: strUrl, kind: 'mpa_channel' });
    this._activeTransition = 'expand';

    const rect = cardEl.getBoundingClientRect();
    const p = this.expandCard(cardEl, rect, () => {
      // Freeze the card clip's current frame so the next document's first
      // paint is exactly what the cover is showing right now.
      const src = getCardVisualSource(cardEl);
      stashChannelHandoff({
        image: captureFrameDataUrl(src.element) || src.imageUrl,
        theme: document.body?.dataset?.theme || null,
      });
      transitionLog('expandFromCard:navigate', { op, url: strUrl });
      window.location.assign(strUrl);
      // Hold the cover while the browser swaps documents. If navigation is
      // somehow blocked, release after a beat so the page isn't stuck.
      return new Promise((resolve) => setTimeout(resolve, 4000));
    });

    return p.finally(() => {
      this._navigating = false;
    });
  }

  /**
   * In-place: full-screen image, then `onComplete` (e.g. WorldManager.open).
   */
  expandCard(cardEl, rect, onComplete) {
    this._debugOp += 1;
    const op = this._debugOp;
    if (!cardEl || !rect || this._inPlaceActive) {
      transitionLog('expandInPlace:skipped', {
        op,
        reason: !cardEl ? 'no card' : !rect ? 'no rect' : 'inPlaceActive',
        inPlaceActive: this._inPlaceActive,
      });
      return Promise.resolve();
    }
    this._inPlaceActive = true;
    transitionLog('expandInPlace:start', { op, growDuration: INPLACE_EXPAND_DURATION });

    this._activeTransition = 'expandInPlace';
    this._expandTl?.kill();
    this._inPlaceLayer?.remove();

    const { layer, promise } = expandCardImageInPlace(cardEl, rect, onComplete);
    this._inPlaceLayer = layer;

    return promise.finally(() => {
      this._inPlaceLayer = null;
      this._activeTransition = null;
      this._inPlaceActive = false;
    });
  }

  destroy() {
    this._tween?.kill();
    this._expandTl?.kill();
    this._inPlaceLayer?.remove();
    this._inPlaceLayer = null;
    this._inPlaceActive = false;
    if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow);
      this._onPageShow = null;
    }
    this._bfcacheBound = false;
  }

  _bindBfcacheReset() {
    if (this._bfcacheBound || typeof window === 'undefined') return;
    this._bfcacheBound = true;
    this._onPageShow = (e) => {
      if (!e.persisted) return;
      transitionLog('transition:bfcache_reset', { path: location.pathname });
      this._navigating = false;
      this._inPlaceActive = false;
      this._tween?.kill();
      this._tween = null;
      this._expandTl?.kill();
      this._expandTl = null;
      this._activeTransition = null;
      this._inPlaceLayer?.remove();
      this._inPlaceLayer = null;
    };
    window.addEventListener('pageshow', this._onPageShow);
  }
}

export const TransitionManager = new TransitionManagerClass();
